// components/kyc/KycInfoDialog.tsx
//
// KYC information dialog (read more)

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Shield, Lock, Eye, Fingerprint, IdCard } from "lucide-react";
import { useMemo } from "react";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

interface KycInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const KycInfoDialog = ({ isOpen, onClose }: KycInfoDialogProps) => {
  const labels = useMemo(() => authLabels(getLanguage()), []);
  const info = labels.kyc?.info;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {info?.title}
          </DialogTitle>
          <DialogDescription>{info?.subtitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold mb-1">{info?.why?.title}</h4>
              <p className="text-sm text-muted-foreground">
                {info?.why?.description}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Fingerprint className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div className="min-w-0 space-y-2">
              <h4 className="font-semibold">{info?.methods?.title}</h4>
              <p className="text-sm text-muted-foreground">
                {info?.methods?.description}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-lg border border-border/70 bg-muted/30 p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <img
                      src="/assets/BankID_logo.png"
                      alt=""
                      className="h-4 w-auto object-contain"
                    />
                    <p className="text-sm font-semibold">
                      {info?.methods?.bankId?.title || "BankID"}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {info?.methods?.bankId?.description}
                  </p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/30 p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <IdCard className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-semibold">
                      {info?.methods?.didit?.title || "Didit KYC"}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {info?.methods?.didit?.description}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-teal-600/20 bg-teal-50/60 dark:bg-teal-950/30 p-3">
                <p className="text-xs font-semibold text-teal-900 dark:text-teal-100 mb-1">
                  {info?.methods?.bankIdBenefits?.title}
                </p>
                <ul className="text-xs text-teal-900/80 dark:text-teal-100/80 space-y-1 list-disc list-inside">
                  <li>{info?.methods?.bankIdBenefits?.items?.login}</li>
                  <li>{info?.methods?.bankIdBenefits?.items?.contracts}</li>
                  <li>{info?.methods?.bankIdBenefits?.items?.preferred}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold mb-1">{info?.security?.title}</h4>
              <p className="text-sm text-muted-foreground">
                {info?.security?.description}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Eye className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold mb-1">{info?.whatChecked?.title}</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside ml-2">
                <li>{info?.whatChecked?.items?.idDocument}</li>
                <li>{info?.whatChecked?.items?.faceMatch}</li>
                <li>{info?.whatChecked?.items?.liveness}</li>
              </ul>
            </div>
          </div>

          <div className="bg-muted/50 rounded-md p-3 mt-2">
            <p className="text-xs text-muted-foreground">{info?.note}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KycInfoDialog;
